import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { ArrowRight, BookOpen, Target, X, Lightbulb } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface NextStep {
  id: string;
  title: string;
  description: string;
  action: string;
  route: string;
  icon: 'practice' | 'notes' | 'mock' | 'review';
  difficulty?: number;
  topicId?: string;
}

export const SmartNextSteps = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<NextStep[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    const loadNextSteps = async () => {
      // Check if dismissed within last 24h
      const dismissedKey = `next-steps-dismissed-${location.pathname}`;
      const lastDismissed = localStorage.getItem(dismissedKey);
      if (lastDismissed && Date.now() - parseInt(lastDismissed) < 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }

      const suggestions = await generateContextualSteps();
      setSteps(suggestions);
    };

    loadNextSteps();
  }, [user, location.pathname]);

  const generateContextualSteps = async (): Promise<NextStep[]> => {
    const pathname = location.pathname;

    // After Practice page
    if (pathname.includes('/practice')) {
      return [
        {
          id: '1',
          title: 'Review Related Notes',
          description: 'Solidify concepts from your recent practice',
          action: 'Read Notes',
          route: '/notes',
          icon: 'notes'
        },
        {
          id: '2',
          title: 'Try Harder Questions',
          description: 'Challenge yourself with advanced MCQs',
          action: 'Practice Hard',
          route: '/practice?difficulty=4',
          icon: 'practice',
          difficulty: 4
        },
        {
          id: '3',
          title: 'Take a Mock Exam',
          description: 'Test your knowledge under exam conditions',
          action: 'Start Mock',
          route: '/mock',
          icon: 'mock'
        }
      ];
    }

    // After Notes page
    if (pathname.includes('/notes')) {
      return [
        {
          id: '1',
          title: 'Practice What You Learned',
          description: 'Apply concepts with targeted MCQs',
          action: 'Start Practice',
          route: '/practice',
          icon: 'practice'
        },
        {
          id: '2',
          title: 'Test Your Understanding',
          description: 'Take a focused mock exam on this topic',
          action: 'Mock Exam',
          route: '/mock',
          icon: 'mock'
        }
      ];
    }

    // After Mock page
    if (pathname.includes('/mock')) {
      const { data: attempts } = await supabase
        .from('attempts')
        .select('mcq_id, is_correct')
        .eq('user_id', user?.id)
        .order('attempted_at', { ascending: false })
        .limit(20);

      const incorrectCount = attempts?.filter(a => !a.is_correct).length || 0;
      const accuracyRate = attempts ? ((attempts.length - incorrectCount) / attempts.length * 100) : 0;

      const suggestions: NextStep[] = [];

      if (accuracyRate < 70) {
        suggestions.push({
          id: '1',
          title: 'Review Weak Areas',
          description: 'Focus on concepts you struggled with',
          action: 'Review Notes',
          route: '/notes',
          icon: 'notes'
        });
      }

      suggestions.push({
        id: '2',
        title: 'Practice Mistakes',
        description: 'Retry questions you got wrong',
        action: 'Practice Again',
        route: '/practice?review=true',
        icon: 'review'
      });

      return suggestions;
    }

    return [];
  };

  const handleAction = async (step: NextStep) => {
    // Track engagement
    if (user) {
      await supabase.from('navigation_events').insert({
        user_id: user.id,
        route: location.pathname,
        action: 'next_step_clicked',
        metadata: { step_id: step.id, step_title: step.title }
      });
    }

    navigate(step.route);
  };

  const handleDismiss = async () => {
    // Track dismissal
    if (user) {
      await supabase.from('navigation_events').insert({
        user_id: user.id,
        route: location.pathname,
        action: 'next_steps_dismissed',
        metadata: { steps_count: steps.length }
      });
    }

    const dismissedKey = `next-steps-dismissed-${location.pathname}`;
    localStorage.setItem(dismissedKey, Date.now().toString());
    setDismissed(true);
  };

  const getIcon = (iconType: NextStep['icon']) => {
    switch (iconType) {
      case 'practice': return Target;
      case 'notes': return BookOpen;
      case 'mock': return Target;
      case 'review': return Lightbulb;
      default: return ArrowRight;
    }
  };

  if (!user || dismissed || steps.length === 0) return null;

  return (
    <Card className="p-6 border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5 relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6"
        onClick={handleDismiss}
      >
        <X className="h-4 w-4" />
      </Button>

      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary/10">
          <Lightbulb className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">What's Next?</h3>
          <p className="text-sm text-muted-foreground">
            Recommended actions based on your recent activity
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {steps.map((step) => {
          const Icon = getIcon(step.icon);
          return (
            <div
              key={step.id}
              className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group"
              onClick={() => handleAction(step)}
            >
              <div className="flex items-center gap-3 flex-1">
                <Icon className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{step.title}</p>
                    {step.difficulty && (
                      <Badge variant="outline" className="text-xs">
                        Level {step.difficulty}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {step.description}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="ml-2 group-hover:bg-primary group-hover:text-primary-foreground"
              >
                {step.action}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
